import { Button, IconButton, TextField } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { DeleteOutline } from '@material-ui/icons';
import _ from 'lodash';
import * as React from 'react';
import ReduxLanguage from '../ReduxLanguage';
import { parseCommaSeparatedValue, serializeCommaSeparatedValue } from '../../utils/ParamConfigUtils';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white',
    padding: 20,
    maxWidth: 640,
    marginBottom: 24,
  },
  inputTitle: {
    marginTop: 0,
    marginBottom: 8,
    fontSize: 18,
    fontWeight: 'bold',
  },
  description: {
    marginBottom: 16,
    color: 'gray',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    borderBottom: '1px solid #eee',
  },
  rowValue: {
    margin: 0,
    fontWeight: 'bold',
  },
  addRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 16,
  },
  addInput: {
    flex: 1,
    marginRight: 12,
  },
  errorText: {
    color: 'red',
    marginTop: 8,
  },
  emptyText: {
    color: 'gray',
    fontStyle: 'italic',
    marginBottom: 8,
  },
});

export interface ICommaSeparatedListParamConfigFormState {
  items: string[];
  newItem: string;
  newItemError: boolean;
}

export interface ICommaSeparatedListParamConfigFormProps extends WithStyles<typeof styles> {
  titleLanguageKey: string;
  descriptionLanguageKey: string;
  initialValue: string;
  hasExistingConfig: boolean;
  onSave: (value: string) => void;
  isSaving: boolean;
  placeholder?: string;
}

class CommaSeparatedListParamConfigForm extends React.Component<ICommaSeparatedListParamConfigFormProps, ICommaSeparatedListParamConfigFormState> {

  constructor(props: ICommaSeparatedListParamConfigFormProps) {
    super(props);
    this.state = {
      items: parseCommaSeparatedValue(props.initialValue || ''),
      newItem: '',
      newItemError: false,
    };
    this.onChangeNewItem = this.onChangeNewItem.bind(this);
    this.onAddItem = this.onAddItem.bind(this);
    this.onRemoveItem = this.onRemoveItem.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  componentDidUpdate(prevProps: ICommaSeparatedListParamConfigFormProps) {
    const { initialValue } = this.props;
    if (prevProps.initialValue !== initialValue) {
      this.setState({
        items: parseCommaSeparatedValue(initialValue || ''),
        newItem: '',
        newItemError: false,
      });
    }
  }

  getSerializedValue(items: string[]): string {
    return serializeCommaSeparatedValue(items);
  }

  hasChanges() {
    const { initialValue } = this.props;
    const { items } = this.state;
    return this.getSerializedValue(items) !== (initialValue || '');
  }

  onChangeNewItem(event: React.ChangeEvent<HTMLInputElement>) {
    const newItem = event.target.value;
    this.setState({ newItem, newItemError: false });
  }

  onAddItem() {
    const { newItem, items } = this.state;
    const trimmedItem = _.trim(newItem);
    if (!trimmedItem) {
      this.setState({ newItemError: true });
      return;
    }
    if (_.includes(items, trimmedItem)) {
      this.setState({ newItem: '', newItemError: false });
      return;
    }
    this.setState({
      items: [...items, trimmedItem],
      newItem: '',
      newItemError: false,
    });
  }

  onRemoveItem(itemToRemove: string) {
    const { items } = this.state;
    this.setState({ items: _.filter(items, (item) => item !== itemToRemove) });
  }

  canSubmitForm() {
    const { isSaving, hasExistingConfig } = this.props;
    const { newItemError } = this.state;
    if (isSaving || newItemError) {
      return false;
    }
    if (!hasExistingConfig) {
      return this.state.items.length > 0;
    }
    return this.hasChanges();
  }

  onSubmit() {
    const { onSave } = this.props;
    const { items } = this.state;
    if (this.canSubmitForm()) {
      onSave(this.getSerializedValue(items));
    }
  }

  render() {
    const { classes, isSaving, titleLanguageKey, descriptionLanguageKey, placeholder } = this.props;
    const { items, newItem, newItemError } = this.state;

    return (
      <div className={classes.container}>
        <h3 className={classes.inputTitle}>
          <ReduxLanguage languageKey={titleLanguageKey} />
        </h3>
        <p className={classes.description}>
          <ReduxLanguage languageKey={descriptionLanguageKey} />
        </p>
        {items.length === 0 ? (
          <p className={classes.emptyText}>
            <ReduxLanguage languageKey="settings.emptyList" />
          </p>
        ) : (
          items.map((item) => (
            <div className={classes.row} key={item}>
              <p className={classes.rowValue}>{item}</p>
              <IconButton onClick={() => this.onRemoveItem(item)} aria-label="remove">
                <DeleteOutline style={{ color: 'darkred' }} />
              </IconButton>
            </div>
          ))
        )}
        <div className={classes.addRow}>
          <TextField
            className={classes.addInput}
            value={newItem}
            onChange={this.onChangeNewItem}
            variant="outlined"
            placeholder={placeholder}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                this.onAddItem();
              }
            }}
          />
          <Button variant="outlined" color="primary" onClick={this.onAddItem}>
            <ReduxLanguage languageKey="settings.addItem" />
          </Button>
        </div>
        {newItemError && (
          <p className={classes.errorText}>
            <ReduxLanguage languageKey="settings.invalidTextValue" />
          </p>
        )}
        <Button
          style={{ marginTop: 20 }}
          variant="contained"
          color="primary"
          disabled={!this.canSubmitForm() || isSaving}
          onClick={this.onSubmit}
        >
          <ReduxLanguage languageKey="settings.save" />
        </Button>
      </div>
    );
  }
}

export default withStyles(styles)(CommaSeparatedListParamConfigForm);
